import { I18nProvider, type I18nProviderProps, importMessages } from "@amzn/awsui-components-react/polaris/i18n";
import { useLocale } from "@amzn/xb-hooks";
import { type PropsWithChildren, useEffect, useState } from "react";

/**
 * Adds the provider for CloudScape's static string defaults
 *
 * See: https://cloudscape.design/get-started/dev-guides/internationalization/
 */

export const CloudscapeLocalizationProvider = ({ children }: PropsWithChildren) => {
  const { locale, isLoading } = useLocale();
  const [messages, setMessages] = useState<I18nProviderProps["messages"]>([]);

  useEffect(() => {
    (async () => {
      if (!isLoading) {
        /**
         * Note: cloudscape currently supports the following (per ref above):
         *   - ar, en, en-GB, de, es, fr, ja, it, pt-BR, ko, zh-CN, zh-TW, id, tr
         *
         * Why does using locale.code below work? (e.g. 'en-US' doesn't match 'en' above)
         *   - the logic used within importMessages parses our input into an array of ['en-us', 'en']
         *     which it then checks against the list above, see https://tiny.amazon.com/cpfdhtnv
         *
         * Why don't we just use locale.language below?
         *   - one edge case where this doesn't work is portuguese, where ours is 'pt' but
         *     Cloudscape specifically requires 'pt-BR'
         */
        const importedMsgs = await importMessages(locale.code);
        setMessages(importedMsgs);
      }
    })();
  }, [locale, isLoading]);

  return (
    <I18nProvider locale={locale.code} messages={messages}>
      {children}
    </I18nProvider>
  );
};
